import { observer } from 'mobx-react';
import React, { useEffect, useMemo, useState } from 'react';
import { FunctionComponent } from 'react';
import {
  DayAvailability,
  SpecialAvailability,
  TempDayAvailability,
} from '../../types';
import TrashIcon from '~/asset/calendar/hours/trash.svg';
import accountStore from '~/store/accountStore';
import medicalCareStore from '~/store/medicalCareStore';
import { Button, Grid, Hidden, IconButton } from '@mui/material';
import { Text } from '~/component/text';
import strings from '~/constants/strings';
import { checkForOverlapping } from '../helpers';
import { DaySetting } from './DaySetting';
import { omit } from 'lodash';
import dayjs from 'dayjs';
import { DaySettingDialog } from '../DaySettingDialog';
import { getAvailabilityText } from '../../HoursView/helpers';

type Props = {};

const styles = {
  container: {
    marginBottom: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#e0e0e0',
    padding: '8px 16px',
    minWidth: 330,
  },
  dayContainer: {
    flex: 1,
  },
  iconButton: {
    width: 32,
    height: 32,
    padding: 4,
  },
  icon: {
    fontSize: 20,
    width: 20,
    height: 20,
  },
  weeklyContainer: {
    // height: 600,
    height: '60vh',
    overFlow: 'hidden',
  },
  saveButton: {
    borderWidth: 1,
    borderStyle: 'solid',
    maxWidth: 400,
    minHeight: 40,
  },
};

const SpecialDayRow: FunctionComponent<{
  day: SpecialAvailability;
}> = observer((props) => {
  const { day } = props;
  const { isLoading } = medicalCareStore;
  const [editDayDialogOpen, setEditDayDialogOpen] = useState(false);

  const onDeleteDay = async () => {
    await medicalCareStore.deleteSpecialDay(day);
  };

  return (
    <Grid
      item
      display={'flex'}
      flexDirection={'row'}
      justifyContent={'space-between'}
      alignItems={'flex-start'}
      className="fullWidth"
      style={styles.container}
    >
      <Button
        sx={{ flex: 1, minHeight: 42 }}
        disabled={isLoading}
        onClick={() => setEditDayDialogOpen(true)}
      >
        <Grid
          item
          display={'flex'}
          flexDirection={'row'}
          justifyContent={'flex-start'}
          alignItems={'flex-start'}
          className=""
          style={styles.dayContainer}
        >
          <Text
            text={`${day.year}年${day.month}月${day.day}日`}
            className="fontWeight--400 mr32"
            style={{ minWidth: 100 }}
          />
          <Grid
            item
            display={'flex'}
            flexDirection={'column'}
            justifyContent={'flex-start'}
            alignItems={'flex-start'}
            className=""
            style={styles.dayContainer}
          >
            {day.hours.map((hour, index) => (
              <Text
                key={index}
                text={getAvailabilityText(hour)}
                className="fontWeight--400"
              />
            ))}
            {day?.hours.length === 0 && (
              <Text text={strings.hours.dayOff} className="fontWeight--400" />
            )}
          </Grid>
        </Grid>
      </Button>
      <Grid
        item
        display={'flex'}
        flexDirection={'column'}
        alignItems={'center'}
        justifyContent={'center'}
        sx={{ height: 42 }}
      >
        <IconButton
          style={styles.iconButton}
          className="color--green"
          onClick={onDeleteDay}
          disabled={isLoading}
        >
          <img style={styles.icon} src={TrashIcon} />
        </IconButton>
      </Grid>
      {editDayDialogOpen && (
        <DaySettingDialog
          replace
          onClose={() => setEditDayDialogOpen(false)}
          defaultDateAvailabilityDate={dayjs(
            `${day.year}-${day.month}-${day.day}`,
            'YYYY-M-D',
          ).format('YYYY-MM-DD')}
        />
      )}
    </Grid>
  );
});

const sortFilterSpecialDays = (days: SpecialAvailability[]) => {
  const copy: SpecialAvailability[] = JSON.parse(JSON.stringify(days));
  const now = dayjs().startOf('day');
  return copy
    .filter((v) =>
      dayjs(`${v.year}-${v.month}-${v.day}`, 'YYYY-M-D')
        .endOf('day')
        .isAfter(now),
    )
    .sort((a, b) => {
      const aInt = parseInt(
        dayjs(`${a.year}-${a.month}-${a.day}`, 'YYYY-M-D').format('YYYYMMDD'),
      );
      const bInt = parseInt(
        dayjs(`${b.year}-${b.month}-${b.day}`, 'YYYY-M-D').format('YYYYMMDD'),
      );
      return aInt - bInt;
    });
};

export const SpecialDayList: FunctionComponent<Props> = observer((props) => {
  const [showSpecialDayDialog, setShowSpecialDayDialog] = useState(false);
  const [specialDays, setSpecialDays] = useState<SpecialAvailability[]>([]);
  const { doctor, pharmacy } = medicalCareStore;
  const businessHours = accountStore.isDoctor
    ? doctor?.businessHours
    : pharmacy?.businessHours;
  const { isLoading } = medicalCareStore;

  useEffect(() => {
    if (businessHours) {
      setSpecialDays(sortFilterSpecialDays(businessHours?.specialDays));
    }
  }, [businessHours]);

  const addNewSpecialDay = (e: React.MouseEvent) => {
    setShowSpecialDayDialog(true);
  };

  return (
    <Grid
      item
      xs={5}
      display={'flex'}
      flexDirection={'column'}
      justifyContent={'flex-start'}
      alignItems={'flex-start'}
      className="borderTop--lightGrey borderLeft--lightGrey"
    >
      <Grid
        item
        display={'flex'}
        className="p32h p16v borderBottom--lightGrey fullWidth"
      >
        <Text text={'営業時間を追加'} className="fontWeight--bold font--16 " />
      </Grid>
      <Grid
        container
        style={styles.weeklyContainer}
        display={'flex'}
        className="overflowYAuto p32h p16v"
        flexDirection={'column'}
        flexWrap={'nowrap'}
        justifyContent={specialDays.length === 0 ? 'center' : 'flex-start'}
        alignItems={'flex-start'}
      >
        {specialDays.map((day, index) => (
          <SpecialDayRow key={index} day={day} />
        ))}
        {specialDays.length === 0 && (
          <Button
            disabled={isLoading}
            className={`btn--lightGrey fullWidth font--18 fontWeight--bold p16v border--lightGrey flexJustifyCenter flexRow`}
            style={styles.saveButton}
            onClick={addNewSpecialDay}
          >
            <Text
              text={'+ 営業時間を追加'}
              className="fontWeight--bold font--16 color--green"
            />
          </Button>
        )}
      </Grid>
      {specialDays.length > 0 && (
        <Grid
          item
          display={'flex'}
          className="p32 borderBottom--lightGrey borderTop--lightGrey fullWidth"
        >
          <Button
            disabled={isLoading}
            className={`btn--lightGrey fullWidth font--18 fontWeight--bold p16v border--lightGrey flexJustifyCenter flexRow`}
            style={styles.saveButton}
            onClick={addNewSpecialDay}
          >
            <Text
              text={'+ 営業時間を追加'}
              className="fontWeight--bold font--16 color--green"
            />
          </Button>
        </Grid>
      )}
      {showSpecialDayDialog && (
        <DaySettingDialog
          replace={false}
          onClose={() => setShowSpecialDayDialog(false)}
          defaultDateAvailabilityDate={dayjs().format('YYYY-MM-DD')}
        />
      )}
    </Grid>
  );
});
